<template>
    <div>
        <v-menu
            v-model="menu"
            offset-y
            :disabled="disabled"
            :close-on-content-click="false"
        >
            <template #activator="{ props }">
                <v-icon
                    v-if="!disabled"
                    v-bind="props"
                    class="cursor-pointer"
                    :class="{ 'grey--text': disabled }"
                    >mdi-chevron-down</v-icon
                >
            </template>

            <v-list>
                <v-list-item
                    v-for="p in periods"
                    :key="p.key"
                    @click="
                        p.key === 'custom'
                            ? openDatePicker()
                            : onSelectPeriod(p.key)
                    "
                    :class="{ 'text-primary': selectedPeriod === p.key }"
                >
                    <v-list-item-title>{{ p.label }}</v-list-item-title>
                </v-list-item>
                <v-expand-transition>
                    <div v-show="showPicker">
                        <v-date-input
                            ref="customRange"
                            v-model="customDateRange"
                            label="Custom Range"
                            variant="solo"
                            multiple="range"
                            class="mt-4 hidden-input"
                            @update:model-value="onSelectPeriod('custom')"
                        />
                    </div>
                </v-expand-transition>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import {
    PERIODS,
    getDateRangeForPeriod,
    getReportData,
} from "@/utils/firestoreUtils";
import { firestore } from "@/firebaseConfig";
import { collection, getDocs, Timestamp } from "firebase/firestore";
import dayjs from "@/plugins/dayjs";

export default {
    name: "ProjectsPeriodFilterMenu",
    props: {
        projectNames: { type: Object, default: () => ({}) },
        priorityMap: { type: Object, default: () => ({}) },
        userId: { type: String, default: null },
        projectIds: { type: Array, default: () => [] },
        disabled: { type: Boolean, default: false },
    },
    emits: ["report-generated", "period-changed"],
    data() {
        return {
            menu: false,
            loading: false,
            selectedPeriod: null,
            customDateRange: [],
            showPicker: false,
            reportRows: [],
            periods: PERIODS,
            localProjectMap: {},
        };
    },
    async created() {
        if (!Object.keys(this.projectNames).length) {
            const snap = await getDocs(collection(firestore, "projects"));
            snap.forEach((doc) => {
                this.localProjectMap[doc.id] = doc.data().name;
            });
        } else {
            this.localProjectMap = { ...this.projectNames };
        }
    },
    methods: {
        /**
         * Opens the custom date range picker by toggling `showPicker`.
         * Uses `$nextTick` to find the nested input element and programmatically clicks it
         * so that the date-picker UI actually appears.
         *
         * @method openDatePicker
         * @returns {void}
         */
        openDatePicker() {
            this.showPicker = true;
            this.$nextTick(() => {
                const inputEl =
                    this.$refs.customRange?.$el?.querySelector("input");
                if (inputEl) inputEl.click();
                else
                    console.warn(
                        "Could not find input element to open v-date-input."
                    );
            });
        },
        /**
         * Fetches report data for the given date range from Firestore (or RTDB fallback).
         * - Shows a loading state.
         * - Logs the query range.
         * - Transforms raw data into `reportRows` with project name, total hours, priority, and active users.
         * - Emits `report-generated` with the shaped rows (or an empty array on error).
         *
         * @async
         * @method fetchReport
         * @param {Timestamp} start - Firestore Timestamp marking the beginning of the range.
         * @param {Timestamp} end - Firestore Timestamp marking the end of the range.
         * @returns {Promise<void>}
         */
        async fetchReport(start, end) {
            this.loading = true;

            try {
                const raw = await getReportData(
                    firestore,
                    start,
                    end,
                    this.localProjectMap,
                    this.userId, // null or “uid” for per-user
                    this.projectIds
                );

                this.reportRows = raw.map((r) => ({
                    projectId: r.projectId,
                    project: this.localProjectMap[r.projectId] || r.projectName,
                    totalHours: r.duration,
                    overtimeHours: r.overtime,
                    priority: this.priorityMap[r.projectId] || "Normal",
                }));

                // emit the freshly‐shaped rows to the parent
                this.$emit("report-generated", this.reportRows, { start, end });
            } catch (err) {
                console.error("Report fetch error:", err);
                this.reportRows = [];
                this.$emit("report-generated", [], { start, end });
            } finally {
                this.loading = false;
            }
        },
        /**
         * Handles selection of a reporting period, whether preset or custom.
         * - Updates internal state (`selectedPeriod`, hides menus/pickers).
         * - Emits `period-changed`.
         * - Determines the `start` and `end` Timestamps:
         *   - For `"custom"`, normalizes the `customDateRange` array to start/end of day.
         *   - For presets, calls `getDateRangeForPeriod`.
         * - Skips Firestore fetch for `today`/`yesterday` if using RTDB shortcuts.
         *
         * @async
         * @method onSelectPeriod
         * @param {string} periodKey - One of the preset keys (`"today"`, `"yesterday"`, `"thisWeek"`, etc.) or `"custom"`.
         * @returns {Promise<void>}
         */
        async onSelectPeriod(periodKey) {
            this.selectedPeriod = periodKey;
            
            let customDateRange = null;
            if (periodKey === "custom") {
                if (!Array.isArray(this.customDateRange)) {
                    console.warn(
                        "Invalid or incomplete customRange:",
                        this.customDateRange
                    );
                    return;
                }
                customDateRange = this.customDateRange;
                this.menu = false;
                this.showPicker = false;
            } else {
                // built-in periods
                this.showPicker = false;
                this.menu = false;
            }

            // Emit period change with custom date range if applicable
            this.$emit("period-changed", periodKey, customDateRange);
        },
    },
};
</script>
<style scoped>
.hidden-input {
    height: 0;
    opacity: 0;
    overflow: hidden;
}
</style>
